import { agentReferenceSchema } from "@wags/agent-contracts/agent-reference.contract.ts";
import { guardedAgentReceiptSchema } from "@wags/agent-contracts/guarded-agent-receipt.contract.ts";
import { digestSchema } from "@wags/agent-contracts/common.contract.ts";
import { agentContextSchema } from "@wags/agent-contracts/context.contract.ts";
import {
    agentLineMigrationIdSchema,
    agentLineNameSchema,
} from "@wags/agent-contracts/line-migration.contract.ts";
import { fileSystemBindingsSchema } from "@wags/filesystem-contracts/filesystem.contract.ts";
import { z } from "zod";

import {
    agentMessageSchema,
    boundedJsonObjectSchema,
    timestampSchema,
} from "./common.contract.ts";
import { agentPublicEventSchema } from "./event.contract.ts";
import { agentTurnSchema } from "./turn.contract.ts";

export const retiredSessionOwnerReaderSchema = z
    .unknown()
    .transform((value) => {
        if (typeof value !== "object" || value === null || Array.isArray(value))
            return value;
        const { owner: _retiredOwner, ...current } = value as Record<
            string,
            unknown
        >;
        return current;
    });

export const retiredKitsContextReaderSchema = z
    .object({
        agentId: z.string(),
        context: z.unknown(),
    })
    .strict()
    .transform(({ agentId, context }) => {
        if (
            !agentId.startsWith("kits-") ||
            typeof context !== "object" ||
            context === null ||
            Array.isArray(context)
        )
            return context;
        const { userId: _retiredUserId, ...current } = context as Record<
            string,
            unknown
        >;
        return current;
    });

export const agentSinkDescriptorSchema = z
    .object({
        sinkId: z.string().min(1).max(256),
        kind: z.string().min(1),
        projection: z.enum([
            "events",
            "messages",
            "session-and-events",
            "session-and-messages",
        ]),
        configuration: boundedJsonObjectSchema,
    })
    .strict();

const uniqueSinkIds = {
    check: (input: { sinks: { sinkId: string }[] }) =>
        new Set(input.sinks.map((sink) => sink.sinkId)).size ===
        input.sinks.length,
    options: { message: "Sink IDs must be unique", path: ["sinks"] },
};

export const agentSessionIdSchema = z.string().min(1).max(512);

export const agentSessionRefSchema = z
    .object({
        agentId: z.string().min(1).max(256),
        sessionId: agentSessionIdSchema,
    })
    .strict();

export const agentSessionParentSchema = retiredSessionOwnerReaderSchema.pipe(
    z
        .object({
            conversationId: z.string().min(1).max(512).optional(),
            sessionId: agentSessionIdSchema,
            agentId: z.string().min(1).max(256).optional(),
            root: agentSessionRefSchema.optional(),
            delegationId: z.string().min(1).max(512),
            turnId: z.string().min(1).max(512).optional(),
            toolCallId: z.string().min(1).max(512).optional(),
        })
        .strict(),
);

export const agentSessionChildPlacementSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("local") }).strict(),
    z.object({ kind: z.literal("fleet") }).strict(),
    z.object({ kind: z.literal("worker") }).strict(),
]);

export const agentSessionChildLinkSchema = z
    .object({
        delegationId: z.string().min(1).max(512),
        childSessionId: agentSessionIdSchema,
        agentId: z.string().min(1).max(256),
        placement: agentSessionChildPlacementSchema,
        turnId: z.string().min(1).max(512).optional(),
        toolCallId: z.string().min(1).max(512).optional(),
        instruction: z.string().max(120_000).optional(),
        capabilities: z.array(z.string().min(1).max(256)).max(32).optional(),
        createdAt: timestampSchema,
        closedAt: timestampSchema.optional(),
        callbackGrant: z
            .object({
                fingerprint: z.string().regex(/^[a-f0-9]{64}$/u),
                kind: z.enum(["refresh", "legacy"]),
                reportingProtocol: z.enum([
                    "terminal",
                    "explicit",
                    "selective",
                ]),
                keepOpenOnCompleted: z.boolean(),
                revokedAt: timestampSchema.optional(),
            })
            .strict()
            .optional(),
    })
    .strict();

const initializeAgentSessionBaseShape = {
    agent: agentReferenceSchema,
    line: agentLineNameSchema.optional(),
    context: agentContextSchema.optional(),
    sessionId: agentSessionIdSchema.optional(),

    filesystemPrincipalId: z.string().min(1).max(512).optional(),

    fileSystems: fileSystemBindingsSchema.optional(),
    initialMetadata: boundedJsonObjectSchema.optional(),
    parent: agentSessionParentSchema.optional(),
    messagingPeers: z.array(agentSessionIdSchema).max(200).optional(),
    sinks: z.array(agentSinkDescriptorSchema),
};

export const pinnedAgentSessionInitializeSchema = z
    .object({
        ...initializeAgentSessionBaseShape,
        releaseId: digestSchema,
        deploymentRevisionId: digestSchema,
        expiresAt: timestampSchema,
    })
    .strict()
    .refine(uniqueSinkIds.check, uniqueSinkIds.options);

export const unpinnedAgentSessionInitializeSchema = z
    .object({
        ...initializeAgentSessionBaseShape,
        expiresAt: timestampSchema.optional(),
    })
    .strict()
    .refine(uniqueSinkIds.check, uniqueSinkIds.options);

export const initializeAgentSessionInputSchema =
    retiredSessionOwnerReaderSchema.pipe(
        z.union([
            pinnedAgentSessionInitializeSchema,
            unpinnedAgentSessionInitializeSchema,
        ]),
    );

export const agentSessionMetadataSchema = retiredSessionOwnerReaderSchema.pipe(
    z
        .object({
            sessionId: z.string().min(1).max(512),
            parent: agentSessionParentSchema.optional(),
            agentId: z.string().min(1).max(256),
            line: agentLineNameSchema.optional(),
            releaseId: digestSchema,
            deploymentRevisionId: digestSchema,
            releasePolicy: z.enum(["pinned", "latest"]).optional(),
            appliedMigrationIds: z.array(agentLineMigrationIdSchema).optional(),
            lifecycle: z.enum(["active", "deleting", "deleted"]),
            automation: z.enum(["active", "escalated", "archived"]),
            metadata: boundedJsonObjectSchema,
            children: z.array(agentSessionChildLinkSchema).optional(),
            createdAt: timestampSchema,
            updatedAt: timestampSchema,
            expiresAt: timestampSchema,
            deletedAt: timestampSchema.optional(),
        })
        .strict(),
);

export const agentSessionCreationReceiptSchema = z
    .object({
        sessionId: z.string().min(1),
        status: z.enum(["created", "existing"]),
        createdAt: timestampSchema,
        expiresAt: timestampSchema,
    })
    .strict();

export const agentSessionDeletionReceiptSchema = z
    .object({
        sessionId: z.string().min(1),
        status: z.enum(["deleting", "deleted", "already-deleted"]),
    })
    .strict();

export const agentSnapshotInputSchema = z
    .object({
        messageLimit: z.number().int().positive().max(200).optional(),
        beforeCursor: z.number().int().positive().optional(),
    })
    .strict();

export const agentSnapshotSchema = z
    .object({
        session: agentSessionMetadataSchema,
        sinks: z
            .array(
                z.object({
                    sinkId: z.string(),
                    attemptCount: z.number().int().nonnegative(),
                    nextAttemptAt: timestampSchema.optional(),
                    lastError: z
                        .object({
                            code: z.string(),
                            message: z.string(),
                        })
                        .optional(),
                }),
            )
            .optional(),
        snapshotCursor: z.number().int().nonnegative(),
        messages: z.array(agentMessageSchema),
        activeTurn: agentTurnSchema.optional(),
        hasMoreMessages: z.boolean(),
        oldestMessageCursor: z.number().int().positive().optional(),
    })
    .strict()
    .refine(
        (snapshot) =>
            snapshot.activeTurn === undefined ||
            snapshot.activeTurn.status === "running",
        { message: "Active turn must be running" },
    );

export const agentEventReadInputSchema = z
    .object({
        afterCursor: z.number().int().nonnegative().optional(),
        limit: z.number().int().positive().max(500).optional(),
        waitMs: z.number().int().nonnegative().max(30_000).optional(),
    })
    .strict();

export const agentEventStreamInputSchema = z
    .object({
        afterCursor: z.number().int().nonnegative().optional(),
    })
    .strict();

export const agentEventPageSchema = z
    .object({
        events: z.array(agentPublicEventSchema),
        headCursor: z.number().int().nonnegative(),
        hasMore: z.boolean(),
    })
    .strict()
    .superRefine((page, context) => {
        for (let index = 1; index < page.events.length; index += 1) {
            if (
                page.events[index]?.cursor !==
                (page.events[index - 1]?.cursor ?? 0) + 1
            ) {
                context.addIssue({
                    code: "custom",
                    path: ["events", index, "cursor"],
                    message: "Event page cursors must be contiguous",
                });
            }
        }
        const lastCursor = page.events.at(-1)?.cursor ?? 0;
        if (lastCursor > page.headCursor) {
            context.addIssue({
                code: "custom",
                path: ["headCursor"],
                message: "Head cursor must include every returned event",
            });
        }
    });

export const agentInputSubmissionResponseSchema = z
    .object({ receipt: guardedAgentReceiptSchema })
    .strict();

export type AgentEventPage = z.infer<typeof agentEventPageSchema>;
export type AgentEventReadInput = z.infer<typeof agentEventReadInputSchema>;
export type AgentEventStreamInput = z.infer<typeof agentEventStreamInputSchema>;
export type AgentSessionCreationReceipt = z.infer<
    typeof agentSessionCreationReceiptSchema
>;
export type AgentSessionDeletionReceipt = z.infer<
    typeof agentSessionDeletionReceiptSchema
>;
export type AgentSessionMetadata = z.infer<typeof agentSessionMetadataSchema>;
export type AgentSessionParent = z.infer<typeof agentSessionParentSchema>;
export type AgentSessionRef = z.infer<typeof agentSessionRefSchema>;
export type AgentSessionChildLink = z.infer<typeof agentSessionChildLinkSchema>;
export type AgentSessionChildPlacement = z.infer<
    typeof agentSessionChildPlacementSchema
>;
export type AgentSinkDescriptor = z.infer<typeof agentSinkDescriptorSchema>;
export type AgentSnapshot = z.infer<typeof agentSnapshotSchema>;
export type AgentSnapshotInput = z.infer<typeof agentSnapshotInputSchema>;
export type InitializeAgentSessionInput = z.infer<
    typeof initializeAgentSessionInputSchema
>;

export function effectiveSessionRoot(
    session: Pick<AgentSessionMetadata, "agentId" | "sessionId" | "parent">,
): AgentSessionRef | undefined {
    return session.parent
        ? session.parent.root
        : { agentId: session.agentId, sessionId: session.sessionId };
}
